import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Optional,
  Output,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBookmark } from '@fortawesome/free-regular-svg-icons';
import { faBookmark as faBookMarkSolid } from '@fortawesome/free-solid-svg-icons';
import { MessageService, TooltipOptions } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { finalize, takeUntil } from 'rxjs';

import { AuthService } from '@usheru-hff/authentication/domain';
import { UserInteractionService } from '@usheru-hff/community-elements/utils';
import { getMovieGenres, getMovieTags, isInMyWishlist, Movie } from '@usheru-hff/shared/domain';
import { UserCtaDirective } from '@usheru-hff/shared/utils-authentication';
import { TrackingService } from '@usheru-hff/sdk/tracking';

@Component({
  selector: 'ui-movie-card-save-button',
  templateUrl: './ui-movie-card-save-button.component.html',
  styleUrls: ['./ui-movie-card-save-button.component.scss'],
  standalone: true,
  imports: [ButtonModule, CommonModule, TooltipModule, UserCtaDirective, FontAwesomeModule],
  // encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiMovieCardSaveButtonComponent {
  @Input() enabled?: boolean;
  @Input({ required: true }) movie!: Movie;
  @Input() type: 'button' | 'icon' = 'button';

  @Output() statusChange = new EventEmitter<boolean>();

  // private readonly messageService = inject(MessageService);
  private readonly userInteractionService = inject(UserInteractionService);
  private readonly trackingService = inject(TrackingService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly authService = inject(AuthService);

  readonly TRACKING_CATEGORY = 'Community';
  readonly TRACKING_ON_SAVE_TO_WISHLIST = 'AddToWishlist';
  readonly TRACKING_ON_REMOVE_FROM_WISHLIST = 'RemoveFromWishlist';

  readonly saveToWishListLabel = $localize`:@@button.to-wishlist:Save`;
  readonly savedInWishListLabel = $localize`:@@button.in-wishlist:Saved`;
  readonly saveToWishListTooltip = $localize`:@@feature-film-interactions.wishlist.add:Save movie to your wishlist`;
  readonly savedInWishListTooltip = $localize`:@@feature-film-interactions.wishlist.saved:Movie saved in your wishlist`;

  loading = false;
  focus = false;
  bookMarkSolidIcon = faBookMarkSolid;
  bookmarkIcon = faBookmark;
  hintOptions: TooltipOptions = {
    showDelay: 500,
    tooltipPosition: 'left'
  };

  destroyed$ = new EventEmitter();

  constructor(@Optional() private messageService: MessageService) {}

  onAuthorizedClick() {}

  addMovieToWishlist() {
    // check if user is logged in
    // add movie to watch list
    this.loading = true;

    const onComplete = () => {
      this.messageService.add({
        severity: 'success',
        summary: $localize`:@@ui-movie-card-save.save.sucess.title:Success`,
        detail: $localize`:@@ui-movie-card-save.save.sucess.description:Movie added to wishlist`
      });

      this.loading = false;

      this.movie.inMyWhislist = true;
      //v2
      this.movie.inMyWishlist = true;
      this.statusChange.emit(true);
      this.trackEvent(this.TRACKING_ON_SAVE_TO_WISHLIST);
    };

    const onError = () => {
      this.messageService.add({
        severity: 'error',
        summary: $localize`:@@ui-movie-card-save.save.error.title:Error`,
        detail: $localize`:@@ui-movie-card-save.save.error.description:Movie could not be added to wishlist`
      });

      this.loading = false;
    };

    this.userInteractionService
      .addMovieToWishList(this.movie.id)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => this.onSubscriptionFinished())
      )
      .subscribe({
        next: () => onComplete(),
        error: () => onError()
      });
  }

  removeMovieFromWishlist() {
    this.loading = true;

    const onComplete = () => {
      this.messageService.add({
        severity: 'success',
        summary: $localize`:@@wishlist-collection.remove-item.success.title:Success`,
        detail: $localize`:@@wishlist-collection.remove-item.success.description:Movie removed from wishlist`
      });

      this.loading = false;
      this.movie.inMyWhislist = false;
      this.movie.inMyWishlist = false;
      this.statusChange.emit(true);
      this.trackEvent(this.TRACKING_ON_REMOVE_FROM_WISHLIST);
    };

    const onError = () => {
      this.messageService.add({
        severity: 'error',
        summary: $localize`:@@wishlist-collection.add-item.error.title:Error`,
        detail: $localize`:@@wishlist-collection.remove-item.error.description:Movie could not be removed from wishlist`
      });

      this.loading = false;
    };

    this.userInteractionService
      .removeMovieFromWishList(this.movie.id)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => this.onSubscriptionFinished())
      )
      .subscribe({
        next: () => onComplete(),
        error: () => onError()
      });
  }

  onToggleButton() {
    if (isInMyWishlist(this.movie)) {
      this.removeMovieFromWishlist();
    } else {
      this.addMovieToWishlist();
    }
  }

  trackEvent(action: string) {
    this.trackingService.trackEvent({
      action: action,
      properties: {
        category: this.TRACKING_CATEGORY,
        // label: '',
        movie: this.movie.title || '',
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie),
        user: this.username
      }
    });
  }

  get isInWishlist() {
    return isInMyWishlist(this.movie) || this.enabled;
  }

  get username() {
    return this.authService.getUserUsername();
  }

  onSubscriptionFinished() {
    this.loading = false;
    this.focus = false;
    this.cd.markForCheck();
  }
}
